import { Platform } from 'react-native';
import { timeoutLogger, TimeoutErrorCode, TimeoutSeverity } from './timeoutLogger';

/**
 * Connection Warming Utility
 *
 * Warms up DNS and TLS connections to critical domains on app startup
 * This reduces latency when making the first real request to these domains
 *
 * Note: Disabled on web due to CORS restrictions on HEAD requests
 */

const CRITICAL_DOMAINS = [
  'https://galaxy-api.galaxydve.com',
  'https://smartvideo-delivnd.galaxydve.com',
];

const TIMEOUT_DURATION = 5000;
const WARMING_TTL_MS = 10 * 60 * 1000;

const warmedDomains: Map<string, number> = new Map();
let inFlightPromise: Promise<void> | null = null;

/**
 * Creates a timeout signal for fetch requests
 * Compatible with React Native and older JavaScript environments
 *
 * @param timeoutMs - Timeout duration in milliseconds
 * @returns AbortController instance with timeout configured
 */
const createTimeoutSignal = (timeoutMs: number): AbortController => {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller;
};

/**
 * Warms a single domain by making a HEAD request
 * This establishes DNS resolution and TLS handshake
 *
 * @param domain - The full domain URL to warm
 */
const warmDomain = async (domain: string): Promise<void> => {
  const lastWarmed = warmedDomains.get(domain);
  if (lastWarmed && Date.now() - lastWarmed < WARMING_TTL_MS) {
    return;
  }

  const requestId = timeoutLogger.generateRequestId();
  const controller = createTimeoutSignal(TIMEOUT_DURATION);

  timeoutLogger.logOperationStart(requestId, 'Connection Warming', { domain });

  const startTime = Date.now();

  try {
    await fetch(domain, {
      method: 'HEAD',
      signal: controller.signal,
    });
    const duration = Date.now() - startTime;

    warmedDomains.set(domain, Date.now());

    timeoutLogger.logOperationComplete(requestId, 'Connection Warming', duration, { domain });
    console.log(`[Connection Warming] Warmed ${domain} in ${duration}ms`);
  } catch (error: any) {
    const duration = Date.now() - startTime;
    const isTimeout = error?.name === 'AbortError' || error?.message?.includes('abort');

    if (isTimeout) {
      await timeoutLogger.logTimeout({
        requestId,
        operationName: `Connection Warming - ${domain}`,
        timeoutDuration: TIMEOUT_DURATION,
        actualDuration: duration,
        errorCode: TimeoutErrorCode.NETWORK_RETRY,
        severity: TimeoutSeverity.MEDIUM,
        metadata: {
          domain,
          errorName: error?.name,
          errorMessage: error?.message,
          fetchType: 'HEAD',
          purpose: 'DNS and TLS warming',
        },
      });
    }

    console.warn(`[Connection Warming] Failed to warm ${domain}:`, error?.message || error);
  }
};

/**
 * Warms up connections to all critical domains
 * This is a fire-and-forget background process that doesn't block homepage loading
 * Should be called once during app initialization
 * Uses inFlight pattern to prevent concurrent warming calls
 */
export const warmCriticalConnections = async (): Promise<void> => {
  if (Platform.OS === 'web') {
    console.log('[Connection Warming] Skipped on web (CORS restrictions)');
    return;
  }

  if (inFlightPromise) {
    return inFlightPromise;
  }

  const executeWarming = async (): Promise<void> => {
    const requestId = timeoutLogger.generateRequestId();
    console.log(`[Connection Warming] [${requestId}] Starting background connection warming for ${CRITICAL_DOMAINS.length} domains`);

    const startTime = Date.now();

    await Promise.all(CRITICAL_DOMAINS.map((domain) => warmDomain(domain)));

    const totalDuration = Date.now() - startTime;
    console.log(`[Connection Warming] [${requestId}] Background warming completed in ${totalDuration}ms`);
  };

  inFlightPromise = executeWarming();

  try {
    await inFlightPromise;
  } finally {
    inFlightPromise = null;
  }
};

/**
 * Warms a custom domain (useful for additional CDN endpoints)
 *
 * @param domain - The domain URL to warm
 */
export const warmCustomDomain = async (domain: string): Promise<void> => {
  if (Platform.OS === 'web' || !domain) {
    return;
  }

  await warmDomain(domain);
};

/**
 * Clears the warming cache to force a new warming cycle
 * Useful after network changes or when connections need to be re-established
 */
export const clearWarmingCache = (): void => {
  warmedDomains.clear();
};
